import { Component, OnInit } from "@angular/core";
import { Industry, Tenant } from "../../models/tenant.model";
import { MatDialog } from "@angular/material/dialog";
import { EditTenantsComponent } from "../master-tenants/edit-tenants/edit-tenants.component";
import { MasterTenantsService } from "../../services/master-tenants.service";
import { debounceTime } from "rxjs/operators";
import { Subject } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { I18nService } from "../../services";
import { environment } from "../../../../environments/environment";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-master-tenants",
  templateUrl: "./master-tenants.component.html",
  styleUrls: ["./master-tenants.component.scss"]
})
export class MasterTenantsComponent implements OnInit {
  tenants: Tenant[] = [];
  filterName = null;
  filterIndustry: number;
  filterCountry: string;
  filterStatus: boolean;
  filterActiveStatus: string;
  filterDate;
  filterModifiedDate;
  filterUpdateDate: string;
  filterContact: boolean;
  filterParentCorporationValue: string;
  selectedFilterValue;
  private filterSubject: Subject<string> = new Subject<string>();
  private filterSubjectModifiedDate: Subject<string> = new Subject<string>();

  industries: Industry[] = [];
  countries = [];
  industryRetails = [];

  page = 1;
  perPage = 100;
  total = 0;

  currentCode: string;
  parentCorporationList = ["ALL", "OFFICE_INDUSTRIAL", "RETAIL"];

  showDropdown = false;
  showDropdownModifiedDate = false;

  filterOptions = [
    {
      label: this.translate.instant("master-tenant.thisQuarter"),
      value: "thisQ"
    },
    {
      label: this.translate.instant("master-tenant.lastQuarter"),
      value: "lastQ"
    }
  ];

  filterDateOptions = {
    year: null,
    thisQuarter: false,
    lastQuarter: false
  };

  filterModifiedDateOptions = {
    yearModifiedDate: null,
    thisQuarterModifiedDate: false,
    lastQuarterModifiedDate: false
  };

  constructor(
    private dialog: MatDialog,
    private masterTenantsService: MasterTenantsService,
    private snackBar: MatSnackBar,
    private i18: I18nService,
    private translate: TranslateService
  ) {
    this.currentCode = environment.platformId;
  }

  ngOnInit(): void {
    this.filterSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.onFilterChange();
    });

    this.filterSubjectModifiedDate.pipe(debounceTime(1000)).subscribe(() => {
      this.onFilterChange();
    });
    this.loadTenants();
    this.getIndustries();
    this.getCountries();
    this.getIndustryReatils();
  }

  loadTenants(): void {
    this.masterTenantsService
      .getMasterTenants(
        this.page - 1,
        this.perPage,
        this.filterName ? this.filterName : null,
        this.filterIndustry,
        this.filterCountry,
        this.selectedFilterValue,
        this.filterStatus,
        this.filterContact,
        this.filterParentCorporationValue,
        this.filterDateOptions,
        this.filterActiveStatus,
        this.filterUpdateDate,
        this.filterModifiedDateOptions
      )
      .then((data: any) => {
        this.tenants = data.content;
        this.total = data.totalElements;
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  }

  getIndustries() {
    this.masterTenantsService.getIndustries().then((resp: Industry[]) => {
      this.industries = resp;
    });
  }

  getIndustryReatils() {
    this.masterTenantsService.getIndustryReatil().then((resp: Industry[]) => {
      this.industryRetails = resp;
    });
  }

  getCountries() {
    this.masterTenantsService.getCountries().then((resp: any) => {
      this.countries = resp;
    });
  }

  editTenant(tenant) {
    const dialogRef = this.dialog.open(EditTenantsComponent, {
      width: "1000px",
      data: {
        tenant: tenant,
        create: false,
        industries: this.industries,
        countries: this.countries,
        industryRetails: this.industryRetails
      }
    });

    dialogRef.afterClosed().subscribe((updatedTenant: Tenant) => {
      if (updatedTenant && updatedTenant.action === "saveTenant") {
        updatedTenant.name = updatedTenant.name.trim();
        this.masterTenantsService
          .updateTenant(updatedTenant)
          .then((response: any) => {
            if (response?.error) {
              this.showMessage("master-tenant.errorName");
            } else {
              this.showMessage("master-tenant.updateMessage");
              this.loadTenants();
            }
          });
      } else if (
        updatedTenant &&
        updatedTenant.action === "Toggle Activation"
      ) {
        this.showMessage("master-tenant.updateMessage");
        this.loadTenants();
      } else if (
        updatedTenant &&
        updatedTenant.action === "error deactivating"
      ) {
        this.showMessage("master-tenant.errorDeactivating");
      } else if (updatedTenant && updatedTenant.action === "mergeCorporation") {
        this.loadTenants();
      }
    });
  }

  openAddDialog() {
    let newTenant = new Tenant();
    const dialogRef = this.dialog.open(EditTenantsComponent, {
      width: "1000px",
      data: {
        tenant: newTenant,
        create: true,
        industries: this.industries,
        countries: this.countries,
        industryRetails: this.industryRetails
      }
    });

    dialogRef.afterClosed().subscribe((newTenant: Tenant) => {
      if (newTenant) {
        newTenant.name = newTenant.name.trim();
        this.masterTenantsService
          .createTenant(newTenant)
          .then((response: any) => {
            if (response?.error) {
              this.showMessage("master-tenant.errorName");
            } else {
              this.showMessage("master-tenant.createMessage");
              this.loadTenants();
            }
          });
      }
    });
  }
  onFilterChange(): void {
    this.loadTenants();
  }

  onInputChange() {
    this.filterSubject.next();
  }

  onInputChangeModifiedDate() {
    this.filterSubjectModifiedDate.next();
  }

  toggleQuarter(quarter: "thisQuarter" | "lastQuarter") {
    this.filterDateOptions[quarter] = !this.filterDateOptions[quarter];
    this.filterSubject.next();
  }

  toggleQuarterModifiedDate(
    quarter: "thisQuarterModifiedDate" | "lastQuarterModifiedDate"
  ) {
    this.filterModifiedDateOptions[quarter] = !this.filterModifiedDateOptions[
      quarter
    ];
    this.filterSubjectModifiedDate.next();
  }

  setUrl(url) {
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }
    return url;
  }

  showMessage(message) {
    this.snackBar.open(this.i18.get(message), "Close", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["custom-snackbar"]
    });
  }

  onPageChange(event) {
    this.page = event;
    this.loadTenants();
  }

  validateContact(tenant) {
    return (
      (tenant.contactName !== null && tenant.contactName !== "") ||
      (tenant.contactEmail !== null && tenant.contactEmail !== "") ||
      (tenant.contactPhone !== null && tenant.contactPhone !== "")
    );
  }

  hideDropdown() {
    setTimeout(() => (this.showDropdown = false), 150);
  }

  hideDropdownModifiedDate() {
    setTimeout(() => (this.showDropdownModifiedDate = false), 150);
  }

  filterNumericInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, "");
    this.filterDate = input.value;
  }

  filterNumericInputModifiedDate(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, "");
    this.filterModifiedDate = input.value;
  }
}
